import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Empty } from "@/components/KpiCard";
import { TagBadge } from "@/components/StatusBadge";
import { PatientFormDialog } from "@/components/PatientFormDialog";
import { useResource } from "@/lib/api";
import { brl, fmtDate, initials } from "@/lib/constants";

export default function Patients() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [debounced, setDebounced] = useState(q);
  const [open, setOpen] = useState(false);

  useEffect(() => { setQ(params.get("q") || ""); }, [params]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => { clearTimeout(t); };
  }, [q]);
  const { data } = useResource(`/patients?q=${encodeURIComponent(debounced)}`);

  return (
    <div>
      <PageHeader title="Pacientes" subtitle={`${data?.length ?? 0} pacientes cadastrados`}>
        <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setOpen(true)} data-testid="patients-new-btn"><UserPlus className="mr-1.5 h-4 w-4" /> Cadastrar paciente</Button>
      </PageHeader>
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, CPF ou telefone" className="h-11 rounded-full bg-white pl-9" data-testid="patients-search-input" />
      </div>
      <div className="surface overflow-x-auto">
        {data && data.length === 0 ? <div className="p-6"><Empty testId="patients-empty">Nenhum paciente encontrado</Empty></div> : (
          <table className="w-full min-w-[720px] text-sm" data-testid="patients-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-5 py-3">Paciente</th><th className="px-3">Telefone</th><th className="px-3">Tratamentos</th><th className="px-3">Última consulta</th><th className="px-3 text-right">Saldo a pagar</th></tr>
            </thead>
            <tbody>
              {(data || []).map((p) => (
                <tr key={p.id} onClick={() => navigate(`/pacientes/${p.id}`)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-sky-50/50" data-testid={`patient-row-${p.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-sky-100 to-cyan-50 text-xs font-bold text-sky-700">{initials(p.name)}</span>
                      <div><p className="font-semibold text-slate-800">{p.name}</p><p className="text-xs text-slate-400">{p.cpf || p.email}</p></div>
                    </div>
                  </td>
                  <td className="font-mono-num px-3 text-slate-600">{p.phone || "—"}</td>
                  <td className="px-3">
                    <div className="flex flex-wrap gap-1">
                      {p.ortho?.active && <TagBadge>Aparelho</TagBadge>}
                      {p.return_plan?.active && <TagBadge>Retorno</TagBadge>}
                    </div>
                  </td>
                  <td className="font-mono-num px-3 text-slate-500">{fmtDate(p.last_visit)}</td>
                  <td className="px-3 text-right">
                    <p className={`font-mono-num font-semibold ${p.overdue > 0 ? "text-rose-600" : "text-slate-800"}`}>{brl(p.balance)}</p>
                    {p.overdue > 0 && <p className="text-[11px] text-rose-500">{brl(p.overdue)} em atraso</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <PatientFormDialog open={open} onOpenChange={setOpen} onSaved={(p) => navigate(`/pacientes/${p.id}`)} />
    </div>
  );
}
